/**
 * Quarantine Rules admin tab — manage the patterns the CRM uses to
 * auto-quarantine inbound leads. Internal+platform-admin only.
 *
 * The rules themselves are LEARNED from past quarantine confirmations
 * (see routes/crm_quarantine.py) — this page doesn't create rules
 * directly, but lets an admin disable / re-enable / delete them when
 * a learned rule has gone stale or false-positives too often.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldWarning, ArrowsClockwise, Trash, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "@/lib/AuthContext";
import { formatLocal } from "@/lib/timezone";

const TYPE_COLORS = {
  domain:   { bg: "#F0F7FF", text: "#1E40AF", border: "#BFDBFE" },
  email:    { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
  phrase:   { bg: "#F3E8FF", text: "#5B21B6", border: "#DDD6FE" },
  default:  { bg: "#F5F5F5", text: "#525252", border: "#E5E5E5" },
};

export default function QuarantineRulesTab() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/crm/quarantine/rules", {
        params: { include_inactive: includeInactive },
      });
      setRules(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load rules");
    }
    setLoading(false);
  }, [includeInactive]);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (rule) => {
    setBusyId(rule.id);
    try {
      await api.patch(`/crm/quarantine/rules/${rule.id}`, {
        is_active: !rule.is_active,
      });
      toast.success(rule.is_active ? "Rule disabled" : "Rule re-enabled");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
    setBusyId(null);
  };

  const destroy = async (rule) => {
    if (!window.confirm(
      `Hard-delete this ${rule.rule_type} rule?\n\nPattern: ${rule.pattern}\n\n` +
      "Future leads matching this pattern will NO longer be quarantined automatically. " +
      "Use Disable instead if you want to keep the audit trail."
    )) return;
    setBusyId(rule.id);
    try {
      await api.delete(`/crm/quarantine/rules/${rule.id}`);
      toast.success("Rule deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
    setBusyId(null);
  };

  const active = rules.filter(r => r.is_active);
  const inactive = rules.filter(r => !r.is_active);
  const totalMatches = rules.reduce((acc, r) => acc + (r.matched_count || 0), 0);

  return (
    <div className="space-y-4" data-testid="quarantine-rules-tab">
      <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="quarantine-rules-card">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] flex items-center gap-1.5">
              <ShieldWarning size={14} />Quarantine rules
            </h2>
            <p className="text-xs text-[#525252] mt-0.5 leading-snug max-w-2xl">
              Patterns the CRM uses to auto-quarantine inbound leads. New rules
              are learned automatically when a reviewer confirms a quarantine —
              you can't add rules from here, but you can disable or delete a
              rule that's gone stale or is causing false positives.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-bold uppercase tracking-wider text-[#525252] flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={e => setIncludeInactive(e.target.checked)}
                data-testid="quarantine-include-inactive"
              />
              Show disabled
            </label>
            <Button onClick={load} disabled={loading} size="sm"
                    className="bg-white border border-[#E5E5E5] text-[#0A0A0A] hover:border-[#0055FF] hover:text-[#0055FF] rounded-none h-8 px-3 text-[11px] uppercase tracking-wider"
                    data-testid="quarantine-refresh">
              <ArrowsClockwise size={12} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-4 text-xs">
          <span className="text-[#737373]">
            <strong className="text-[#0A0A0A]">{active.length}</strong> active
          </span>
          <span className="text-[#737373]">
            <strong className="text-[#0A0A0A]">{inactive.length}</strong> disabled
          </span>
          <span className="text-[#737373]">
            <strong className="text-[#0A0A0A]">{totalMatches}</strong> total matches all-time
          </span>
        </div>

        {rules.length === 0 && !loading && (
          <p className="text-xs text-[#A3A3A3]">No rules learned yet.</p>
        )}

        {rules.length > 0 && (
          <div className="overflow-x-auto border border-[#F5F5F5]">
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAFA]">
                <tr className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#A3A3A3]">
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-left px-3 py-2.5">Pattern</th>
                  <th className="text-right px-3 py-2.5">Matches</th>
                  <th className="text-left px-3 py-2.5">Last match</th>
                  <th className="text-left px-3 py-2.5">Notes</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(r => {
                  const c = TYPE_COLORS[r.rule_type] || TYPE_COLORS.default;
                  return (
                    <tr key={r.id}
                        className={`border-t border-[#F5F5F5] ${r.is_active ? "" : "bg-[#FAFAFA] opacity-70"}`}
                        data-testid={`quarantine-rule-${r.id}`}>
                      <td className="px-3 py-2.5 align-top">
                        <span className="inline-block px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                              style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
                          {r.rule_type}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 align-top font-mono text-[11px] max-w-md break-all">
                        {r.pattern}
                      </td>
                      <td className="px-3 py-2.5 align-top text-right text-[#525252]">
                        {r.matched_count || 0}
                      </td>
                      <td className="px-3 py-2.5 align-top whitespace-nowrap text-[11px] text-[#525252]">
                        {r.last_matched_at ? formatLocal(r.last_matched_at, user) : "—"}
                      </td>
                      <td className="px-3 py-2.5 align-top text-[11px] text-[#737373] max-w-xs break-words">
                        {r.notes || "—"}
                      </td>
                      <td className="px-3 py-2.5 align-top text-right whitespace-nowrap">
                        <Button onClick={() => toggleActive(r)} disabled={busyId === r.id} size="sm"
                                className="bg-white border border-[#E5E5E5] text-[#0A0A0A] hover:border-[#0055FF] hover:text-[#0055FF] rounded-none h-7 px-2 text-[10px] uppercase tracking-wider mr-1"
                                data-testid={`quarantine-rule-toggle-${r.id}`}>
                          {r.is_active ? <><EyeSlash size={10} className="mr-1" />Disable</> : <><Eye size={10} className="mr-1" />Enable</>}
                        </Button>
                        <Button onClick={() => destroy(r)} disabled={busyId === r.id} size="sm"
                                className="bg-white border border-[#E5E5E5] text-[#DC2626] hover:border-[#DC2626] rounded-none h-7 px-2 text-[10px] uppercase tracking-wider"
                                data-testid={`quarantine-rule-delete-${r.id}`}>
                          <Trash size={10} className="mr-1" />Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
